import React, { useEffect, useMemo, useRef, useState } from 'react'
import { AlertTriangle, ChevronDown, ChevronUp, HelpCircle, Maximize2 } from 'lucide-react'
import useStore, { isComparing, resolveActivePlanKey } from '@/stores/useStore'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader } from '@/components/ui/card'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip'
import { exportCalculationsToCSv } from '@/lib/csv'
import { escapeCssValue, formatCurrency, formatPercent } from '@/lib/utils'
import { isTerminatedEmployee } from '@/lib/calculations'
import { FOCUS_EMPLOYEE_OUTPUT_EVENT } from '@/lib/employeeFocus'
import { useEmployeeTableView } from '@/hooks/useEmployeeTableView'
import SortableHeader from '@/components/employee-table/SortableHeader'
import EmployeeTableControls from '@/components/employee-table/EmployeeTableControls'
import PlanChips from '@/components/PlanChips'
import EmptyState from '@/components/employee-table/EmptyState'
import { Expandable, ExpandableScroll, ExpandableFill, PaginatedRows, ShowMoreBar, ScrollHint, useExpanded } from '@/components/ui/expandable'
import { isCashBalanceEnabled } from '@/lib/planDesign'

// Rows-per-page in the popped-out results table. Mounting a whole large census
// of multi-column rows in one synchronous commit spikes memory enough to crash
// the renderer ("Aw, Snap"); cap the page so each commit is bounded and let the
// pagination controls step through the rest.
const RESULTS_PAGE_SIZE = 100

function getIneligibilityReasons(emp) {
  return emp.eligibility?.reasons || []
}

function EligibilityBadge({ emp }) {
  const status = emp.eligibility?.status
  const reasons = getIneligibilityReasons(emp)
  const unknown = status === 'unknown'
  const eligible = status === 'eligible'
  const label = eligible ? 'Eligible' : unknown ? 'Unknown' : 'Ineligible'
  const classes = eligible
    ? 'border-emerald-200 bg-emerald-50 text-emerald-700'
    : unknown
      ? 'border-amber-200 bg-amber-50 text-amber-700'
      : 'border-red-200 bg-red-50 text-red-700'

  if (eligible) {
    return (
      <span className={`inline-flex shrink-0 items-center rounded border px-1.5 py-0.5 text-[10px] font-medium ${classes}`}>
        {label}
      </span>
    )
  }

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <span className={`inline-flex shrink-0 cursor-help items-center rounded border px-1.5 py-0.5 text-[10px] font-medium ${classes}`}>
            {label}
            <HelpCircle className="ml-0.5 h-2.5 w-2.5" />
          </span>
        </TooltipTrigger>
        <TooltipContent className="max-w-xs">
          <p className="font-medium mb-1">
            {unknown
              ? 'Employer contributions were modeled despite missing required fields:'
              : 'Not eligible for employer contributions:'}
          </p>
          <ul className="list-disc pl-4 space-y-0.5">
            {reasons.map((reason, i) => <li key={i}>{reason}</li>)}
          </ul>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  )
}

function ProrationBadge({ employee }) {
  if (!employee.compWasProrated) return null

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <span className="ml-1 inline-flex cursor-help items-center rounded border border-sky-200 bg-sky-50 px-1.5 py-0.5 text-[10px] font-medium text-sky-700">
            Prorated
          </span>
        </TooltipTrigger>
        <TooltipContent className="max-w-xs">
          <p>
            Entered annualized salary: {formatCurrency(employee.rawCompensation)}.
            {' '}Modeled plan-year compensation: {formatCurrency(employee.compensation)}.
          </p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  )
}

function UnresolvedVestingBadge({ employee, compact = false }) {
  if (!(employee.vestingUnresolvedAmount > 0)) return null
  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <span className="ml-1 inline-flex cursor-help items-center rounded border border-amber-200 bg-amber-50 px-1.5 py-0.5 text-[10px] font-medium text-amber-700">
            {compact ? 'Unresolved' : `${formatCurrency(employee.vestingUnresolvedAmount)} unresolved`}
          </span>
        </TooltipTrigger>
        <TooltipContent className="max-w-xs">
          <p>
            Missing service prevents vesting from being determined for: {employee.vestingUnresolvedSources.join(', ')}.
          </p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  )
}

function ProfitSharingCell({ employee }) {
  const total = formatCurrency(employee.profitSharingAmount)
  const components = employee.profitSharingComponents || []
  if (!employee.profitSharingLimitedBy415c && components.length <= 1 && !employee.ageWeightedProfitSharingAmount) return <>{total}</>

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <span className="inline-flex cursor-help items-center gap-1">
            {total}
            <AlertTriangle className="h-3 w-3 text-amber-600" />
          </span>
        </TooltipTrigger>
        <TooltipContent className="max-w-xs">
          <div className="space-y-1">
            {components.map((component) => (
              <p key={component.componentId}>
                {component.type.replaceAll('_', ' ')}{component.groupLabel !== undefined ? ` · ${component.groupLabel || 'Everyone else'}` : ''}: {formatCurrency(component.allocated)} requested {formatCurrency(component.requested)}{component.testingAge ? ` · testing age ${component.testingAge}` : ''}{component.capLimited ? ' · cap-limited' : ''}
              </p>
            ))}
            {employee.profitSharingLimitedBy415c && <p>Combined profit sharing was reduced to stay within §415(c).</p>}
          </div>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  )
}

function PaginationControls({ page, pageCount, total, start, end, onPageChange }) {
  // Inline the table is capped to a handful of rows (RowLimit), so paging only
  // makes sense in the expanded pop-out where every row renders.
  const expanded = useExpanded()
  if (!expanded || total <= RESULTS_PAGE_SIZE) return null

  return (
    <div className="flex flex-wrap items-center justify-between gap-2 text-xs text-muted-foreground">
      <span>
        Showing {start + 1}-{end} of {total}
      </span>
      <div className="flex items-center gap-2">
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="h-7"
          onClick={() => onPageChange(Math.max(0, page - 1))}
          disabled={page === 0}
        >
          Previous
        </Button>
        <span className="min-w-[4rem] text-center">
          Page {page + 1} of {pageCount}
        </span>
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="h-7"
          onClick={() => onPageChange(Math.min(pageCount - 1, page + 1))}
          disabled={page >= pageCount - 1}
        >
          Next
        </Button>
      </div>
    </div>
  )
}

// Compact per-employee change vs Plan A, rendered as a second line inside a
// currency cell while a non-baseline plan is displayed.
function RowDelta({ value }) {
  if (!Number.isFinite(value) || Math.abs(value) < 0.005) return null
  const higher = value > 0
  return (
    <span className={`block whitespace-nowrap text-[10px] leading-tight ${higher ? 'text-emerald-700' : 'text-red-700'}`}>
      {higher ? '▲' : '▼'} {formatCurrency(Math.abs(value))}
    </span>
  )
}

export default function EmployeeDetails({ exportResultsRef, reportMode = false }) {
  const comparing = useStore(isComparing)
  // While comparing, the shared plan chips pick which plan's results this table
  // shows. Everything below derives from that one active plan.
  const activePlanKey = useStore(resolveActivePlanKey)
  const activePlan = useStore((state) => state[activePlanKey])
  const activeAnalysis = useStore((state) => state.analysis[activePlanKey])
  const baselineEmployees = useStore((state) => state.analysis.planA.calculatedEmployees)
  const calculatedEmployees = useMemo(
    () => activeAnalysis?.calculatedEmployees || [],
    [activeAnalysis],
  )
  const metrics = activeAnalysis?.metrics
  const activeContributions = activePlan.employerContributions
  const activeTerminationHandling = activePlan.terminationHandling
  const activeVesting = activePlan.vesting
  const afterTaxAllowed = Boolean(activePlan.afterTax?.allowed)
  const cashBalanceEnabled = isCashBalanceEnabled(activePlan)

  // Per-employee change vs Plan A on the employer/total columns – the numbers
  // people actually compare designs on. Only meaningful off the baseline.
  const showDeltas = comparing && activePlanKey !== 'planA'
  const baselineById = useMemo(() => (
    showDeltas ? new Map(baselineEmployees.map((emp) => [emp.id, emp])) : null
  ), [showDeltas, baselineEmployees])

  const {
    matchEnabled,
    necEnabled,
    profitSharingEnabled,
  } = activeContributions
  const qnecShown = calculatedEmployees.some((employee) => (Number(employee.qnecAmount) || 0) > 0)
  const allocationGroup = React.useCallback((employee) => (
    profitSharingEnabled ? employee.profitSharingGroupLabel : employee.allocationGroupLabel
  ) || '', [profitSharingEnabled])

  // Sort descriptors for every sortable column (numeric + text). Keys are stable
  // ids passed to SortableHeader/toggleSort.
  const columns = useMemo(() => ({
    // Text columns
    id: { type: 'text', accessor: (emp) => emp.id },
    name: { type: 'text', accessor: (emp) => emp.name },
    gender: { type: 'text', accessor: (emp) => emp.gender },
    hce: { type: 'text', accessor: (emp) => (emp.isHCE ? 'Yes' : 'No') },
    allocationGroup: { type: 'text', accessor: allocationGroup },
    status: { type: 'text', accessor: (emp) => emp.status || 'Active' },
    // Numeric columns
    age: { type: 'number', accessor: (emp) => emp.age },
    tenure: { type: 'number', accessor: (emp) => emp.tenure?.totalMonths },
    compensation: { type: 'number', accessor: (emp) => emp.compensation },
    deferral: { type: 'number', accessor: (emp) => emp.totalDeferralPercent },
    pretax: { type: 'number', accessor: (emp) => emp.actualPretax },
    roth: { type: 'number', accessor: (emp) => emp.actualRoth },
    catchup: { type: 'number', accessor: (emp) => emp.catchupContribution },
    employeeTotal: { type: 'number', accessor: (emp) => emp.totalEmployeeContribution },
    match: { type: 'number', accessor: (emp) => emp.matchAmount },
    nec: { type: 'number', accessor: (emp) => emp.necAmount },
    qnec: { type: 'number', accessor: (emp) => emp.qnecAmount },
    profitSharing: { type: 'number', accessor: (emp) => emp.profitSharingAmount },
    employer: { type: 'number', accessor: (emp) => emp.dcEmployerCost },
    cashBalance: { type: 'number', accessor: (emp) => emp.cashBalanceCredit },
    afterTax: { type: 'number', accessor: (emp) => emp.afterTaxContribution },
    eligibility: { type: 'text', accessor: (emp) => emp.eligibility?.status || 'unknown' },
    serviceYears: { type: 'number', accessor: (emp) => emp.serviceYears },
    vested: { type: 'number', accessor: (emp) => emp.vestedEmployerTotal },
    unvested: { type: 'number', accessor: (emp) => emp.unvestedEmployerTotal },
    total: { type: 'number', accessor: (emp) => emp.totalContribution },
  }), [allocationGroup])

  const allocationGroupOptions = useMemo(() => (
    [...new Set(calculatedEmployees.map((emp) => String(allocationGroup(emp)).trim()).filter(Boolean))]
      .sort((a, b) => a.localeCompare(b))
  ), [allocationGroup, calculatedEmployees])

  // Filter definitions for the discrete-valued columns.
  const filters = useMemo(() => ([
    {
      key: 'status',
      label: 'Status',
      getValue: (emp) => emp.status || 'Active',
      options: ['Active', 'Leave', 'Terminated'],
    },
    {
      key: 'hce',
      label: 'HCE',
      getValue: (emp) => (emp.isHCE ? 'Yes' : 'No'),
      options: ['Yes', 'No'],
    },
    {
      key: 'gender',
      label: 'Gender',
      getValue: (emp) => emp.gender || 'U',
      options: ['M', 'F', 'U'],
    },
    {
      key: 'eligibility',
      label: 'Eligibility',
      getValue: (emp) => ({
        eligible: 'Eligible',
        ineligible: 'Ineligible',
        unknown: 'Unknown',
      })[emp.eligibility?.status] || 'Unknown',
      options: ['Eligible', 'Ineligible', 'Unknown'],
    },
    {
      key: 'allocationGroup',
      label: profitSharingEnabled ? 'Profit-sharing group' : 'Allocation Group',
      getValue: (emp) => String(allocationGroup(emp)).trim(),
      options: allocationGroupOptions,
    },
  ]), [allocationGroup, allocationGroupOptions, profitSharingEnabled])

  const getSearchText = useMemo(
    () => (emp) => `${emp.id ?? ''} ${emp.name ?? ''} ${allocationGroup(emp)}`,
    [allocationGroup]
  )

  const {
    viewRows,
    search,
    setSearch,
    activeFilters,
    setFilter,
    clearAll,
    sort,
    toggleSort,
    totalCount,
    activeCount,
    isActive,
  } = useEmployeeTableView({
    rows: calculatedEmployees,
    getSearchText,
    filters,
    columns,
  })

  useEffect(() => {
    if (!exportResultsRef) return undefined

    exportResultsRef.current = () => exportCalculationsToCSv(viewRows, {
      estimate: useStore.getState().workspaceMode === 'estimate',
    })
    return () => {
      exportResultsRef.current = null
    }
  }, [exportResultsRef, viewRows])

  const [isResultsExpanded, setIsResultsExpanded] = useState(true)
  const [isResultsPoppedOut, setIsResultsPoppedOut] = useState(false)
  const [currentPage, setCurrentPage] = useState(0)
  const pageCount = Math.max(1, Math.ceil(viewRows.length / RESULTS_PAGE_SIZE))
  const activePage = Math.min(currentPage, pageCount - 1)
  const pageStart = activePage * RESULTS_PAGE_SIZE
  const pageEnd = Math.min(viewRows.length, pageStart + RESULTS_PAGE_SIZE)
  const visibleEmployees = useMemo(
    () => viewRows.slice(pageStart, pageEnd),
    [viewRows, pageStart, pageEnd]
  )

  // Reset to the first page when the search query or filters change (Req 4.2).
  useEffect(() => {
    setCurrentPage(0)
  }, [search, activeFilters])

  // Clamp the active page when the view shrinks (Req 4.4).
  useEffect(() => {
    setCurrentPage((page) => Math.min(page, pageCount - 1))
  }, [pageCount])

  // "Go to Outputs row" from the Summary banners. Clear any filters, pop the
  // results out (so every row renders regardless of the inline row cap), then
  // the effect below pages to the row and flashes it.
  const pendingOutputFocusRef = useRef(null)
  useEffect(() => {
    const handleFocusOutput = (event) => {
      const employeeId = event.detail?.employeeId
      if (employeeId === null || employeeId === undefined || employeeId === '') return
      pendingOutputFocusRef.current = String(employeeId)
      clearAll()
      setIsResultsExpanded(true)
      setIsResultsPoppedOut(true)
    }
    window.addEventListener(FOCUS_EMPLOYEE_OUTPUT_EVENT, handleFocusOutput)
    return () => window.removeEventListener(FOCUS_EMPLOYEE_OUTPUT_EVENT, handleFocusOutput)
  }, [clearAll])

  useEffect(() => {
    const targetId = pendingOutputFocusRef.current
    if (!targetId || !isResultsPoppedOut) return undefined

    const targetIndex = viewRows.findIndex((emp) => String(emp.id) === targetId)
    if (targetIndex < 0) {
      pendingOutputFocusRef.current = null
      return undefined
    }

    const nextPage = Math.floor(targetIndex / RESULTS_PAGE_SIZE)
    if (activePage !== nextPage) {
      setCurrentPage(nextPage)
      return undefined
    }

    let frame
    let attempts = 0
    const locate = () => {
      const selector = `[data-output-employee-id="${escapeCssValue(targetId)}"]`
      // The row renders twice (inline preview + popped-out modal). Scope to the
      // modal when open so we don't flash the hidden inline copy before the
      // modal's content mounts.
      const modalRoot = document.querySelector('[data-slot="dialog-content"]')
      const element = (modalRoot || document).querySelector(selector)
      if (!element) {
        // The popped-out table mounts a frame after the dialog opens, so retry
        // briefly rather than giving up on the first frame.
        if (attempts++ < 30) frame = requestAnimationFrame(locate)
        return
      }
      element.scrollIntoView({ block: 'center', behavior: 'smooth' })
      element.classList.add('output-row-flash')
      window.setTimeout(() => element.classList.remove('output-row-flash'), 1800)
      pendingOutputFocusRef.current = null
    }
    frame = requestAnimationFrame(locate)

    return () => cancelAnimationFrame(frame)
  }, [activePage, isResultsPoppedOut, viewRows])

  const getEmploymentClass = (status) => {
    if (status === 'Active') return 'bg-emerald-50 text-emerald-700 border border-emerald-200'
    if (status === 'Leave') return 'bg-amber-50 text-amber-700 border border-amber-200'
    return 'bg-muted text-muted-foreground border border-border'
  }

  const toggleResultsExpanded = () => setIsResultsExpanded(isExpanded => !isExpanded)

  // Fixed columns (17) plus the conditionally-rendered ones, used as the
  // empty-state row's colSpan.
  const columnCount =
    17 +
    (matchEnabled ? 1 : 0) +
    (necEnabled ? 1 : 0) +
    (qnecShown ? 1 : 0) +
    (profitSharingEnabled ? 1 : 0) +
    (cashBalanceEnabled ? 1 : 0) +
    (afterTaxAllowed ? 1 : 0) +
    (activeVesting.enabled ? 3 : 0)

  return (
    <div id="employee-details" className="space-y-4">
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-start justify-between gap-3">
            <button
              type="button"
              onClick={reportMode ? undefined : toggleResultsExpanded}
              disabled={reportMode}
              className="-mx-1 flex min-w-0 flex-1 flex-col items-start gap-1 rounded px-1 text-left transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
              aria-expanded={reportMode || isResultsExpanded}
              aria-controls="employee-results-table"
            >
              <span className="text-base font-medium text-foreground">Calculated results</span>
              <span className="text-xs/relaxed text-muted-foreground">
                Employer contributions include match, NEC, QNEC, and profit sharing (where enabled).
                Terminated employees {activeTerminationHandling.includeInMetrics ? 'are' : 'are not'} included in metrics (per plan settings).
              </span>
            </button>
            {!reportMode && <div className="flex shrink-0 items-center gap-1">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => { setIsResultsExpanded(true); setIsResultsPoppedOut(true) }}
                className="h-7 px-2"
                aria-label="Open calculated results in a larger view"
                title="Expand"
              >
                <Maximize2 className="h-4 w-4" />
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={toggleResultsExpanded}
                className="h-7 px-2"
                aria-expanded={isResultsExpanded}
                aria-controls="employee-results-table"
                aria-label={isResultsExpanded ? 'Collapse calculated employee results' : 'Expand calculated employee results'}
              >
                {isResultsExpanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
              </Button>
            </div>}
          </div>
        </CardHeader>

        {(reportMode || isResultsExpanded) && (
          <CardContent className="pt-0">
            <Expandable
              title="Calculated results"
              description="Employer contributions include match, NEC, QNEC, and profit sharing (where enabled)."
              showTrigger={false}
              open={isResultsPoppedOut}
              onOpenChange={setIsResultsPoppedOut}
              modalLayout="fit"
            >
            <div className="mb-3 space-y-2">
              {comparing && (
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-xs text-muted-foreground">Showing results for</span>
                  <PlanChips />
                  {showDeltas && (
                    <span className="text-[11px] text-muted-foreground">
                      Employer and Total show each employee&apos;s change vs Plan A.
                    </span>
                  )}
                </div>
              )}
              <EmployeeTableControls
                search={search}
                onSearchChange={setSearch}
                filters={filters}
                activeFilters={activeFilters}
                onFilterChange={setFilter}
                onClear={clearAll}
                isActive={isActive}
                activeCount={activeCount}
                totalCount={totalCount}
              />
              <PaginationControls
                page={activePage}
                pageCount={pageCount}
                total={viewRows.length}
                start={pageStart}
                end={pageEnd}
                onPageChange={setCurrentPage}
              />
            </div>
            <ExpandableFill>
            <ExpandableScroll
              id="employee-results-table"
              className="rounded-lg border"
              collapsedClassName={visibleEmployees.length > 5 ? 'overflow-x-auto rounded-b-none border-b-0' : 'overflow-x-auto'}
              expandedClassName="min-h-0 flex-1 overflow-auto"
            >
              <table className="data-table data-table-output text-xs">
                <thead className="sticky top-0 z-10">
                  <tr>
                    <SortableHeader
                      columnKey="id"
                      active={sort.key === 'id'}
                      dir={sort.key === 'id' ? sort.dir : null}
                      onToggle={toggleSort}
                      className="sticky-col sticky-employee-id min-w-[110px]"
                    >
                      ID
                    </SortableHeader>
                    <SortableHeader
                      columnKey="name"
                      active={sort.key === 'name'}
                      dir={sort.key === 'name' ? sort.dir : null}
                      onToggle={toggleSort}
                      className="sticky-col sticky-employee-name"
                    >
                      Name
                    </SortableHeader>
                    <SortableHeader
                      columnKey="gender"
                      active={sort.key === 'gender'}
                      dir={sort.key === 'gender' ? sort.dir : null}
                      onToggle={toggleSort}
                    >
                      Gender
                    </SortableHeader>
                    <SortableHeader
                      columnKey="age"
                      active={sort.key === 'age'}
                      dir={sort.key === 'age' ? sort.dir : null}
                      align="right"
                      onToggle={toggleSort}
                    >
                      Age
                    </SortableHeader>
                    <SortableHeader
                      columnKey="tenure"
                      active={sort.key === 'tenure'}
                      dir={sort.key === 'tenure' ? sort.dir : null}
                      align="right"
                      onToggle={toggleSort}
                    >
                      Tenure
                    </SortableHeader>
                    <SortableHeader
                      columnKey="compensation"
                      active={sort.key === 'compensation'}
                      dir={sort.key === 'compensation' ? sort.dir : null}
                      align="right"
                      onToggle={toggleSort}
                    >
                      Compensation
                    </SortableHeader>
                    <SortableHeader
                      columnKey="hce"
                      active={sort.key === 'hce'}
                      dir={sort.key === 'hce' ? sort.dir : null}
                      onToggle={toggleSort}
                    >
                      HCE
                    </SortableHeader>
                    <SortableHeader
                      columnKey="allocationGroup"
                      active={sort.key === 'allocationGroup'}
                      dir={sort.key === 'allocationGroup' ? sort.dir : null}
                      onToggle={toggleSort}
                    >
                      {profitSharingEnabled ? 'Profit-sharing group' : 'Allocation Group'}
                    </SortableHeader>
                    <SortableHeader
                      columnKey="deferral"
                      active={sort.key === 'deferral'}
                      dir={sort.key === 'deferral' ? sort.dir : null}
                      align="right"
                      onToggle={toggleSort}
                    >
                      Deferral %
                    </SortableHeader>
                    <SortableHeader
                      columnKey="pretax"
                      active={sort.key === 'pretax'}
                      dir={sort.key === 'pretax' ? sort.dir : null}
                      align="right"
                      onToggle={toggleSort}
                    >
                      Pre-tax
                    </SortableHeader>
                    <SortableHeader
                      columnKey="roth"
                      active={sort.key === 'roth'}
                      dir={sort.key === 'roth' ? sort.dir : null}
                      align="right"
                      onToggle={toggleSort}
                    >
                      Roth
                    </SortableHeader>
                    <SortableHeader
                      columnKey="catchup"
                      active={sort.key === 'catchup'}
                      dir={sort.key === 'catchup' ? sort.dir : null}
                      align="right"
                      onToggle={toggleSort}
                    >
                      Catch-up
                    </SortableHeader>
                    <SortableHeader
                      columnKey="employeeTotal"
                      active={sort.key === 'employeeTotal'}
                      dir={sort.key === 'employeeTotal' ? sort.dir : null}
                      align="right"
                      onToggle={toggleSort}
                    >
                      Employee total
                    </SortableHeader>
                    {matchEnabled && (
                      <SortableHeader
                        columnKey="match"
                        active={sort.key === 'match'}
                        dir={sort.key === 'match' ? sort.dir : null}
                        align="right"
                        onToggle={toggleSort}
                      >
                        Match
                      </SortableHeader>
                    )}
                    {necEnabled && (
                      <SortableHeader
                        columnKey="nec"
                        active={sort.key === 'nec'}
                        dir={sort.key === 'nec' ? sort.dir : null}
                        align="right"
                        onToggle={toggleSort}
                      >
                        NEC
                      </SortableHeader>
                    )}
                    {qnecShown && (
                      <SortableHeader
                        columnKey="qnec"
                        active={sort.key === 'qnec'}
                        dir={sort.key === 'qnec' ? sort.dir : null}
                        align="right"
                        onToggle={toggleSort}
                      >
                        QNEC
                      </SortableHeader>
                    )}
                    {profitSharingEnabled && (
                      <SortableHeader
                        columnKey="profitSharing"
                        active={sort.key === 'profitSharing'}
                        dir={sort.key === 'profitSharing' ? sort.dir : null}
                        align="right"
                        onToggle={toggleSort}
                      >
                        Profit sharing
                      </SortableHeader>
                    )}
                    <SortableHeader
                      columnKey="employer"
                      active={sort.key === 'employer'}
                      dir={sort.key === 'employer' ? sort.dir : null}
                      align="right"
                      onToggle={toggleSort}
                    >
                      Employer
                    </SortableHeader>
                    {cashBalanceEnabled && (
                      <SortableHeader
                        columnKey="cashBalance"
                        active={sort.key === 'cashBalance'}
                        dir={sort.key === 'cashBalance' ? sort.dir : null}
                        align="right"
                        onToggle={toggleSort}
                      >
                        Cash balance
                      </SortableHeader>
                    )}
                    {afterTaxAllowed && (
                      <SortableHeader
                        columnKey="afterTax"
                        active={sort.key === 'afterTax'}
                        dir={sort.key === 'afterTax' ? sort.dir : null}
                        align="right"
                        onToggle={toggleSort}
                      >
                        After-tax
                      </SortableHeader>
                    )}
                    <SortableHeader
                      columnKey="eligibility"
                      active={sort.key === 'eligibility'}
                      dir={sort.key === 'eligibility' ? sort.dir : null}
                      onToggle={toggleSort}
                    >
                      Eligibility
                    </SortableHeader>
                    {activeVesting.enabled && (
                      <>
                        <SortableHeader
                          columnKey="serviceYears"
                          active={sort.key === 'serviceYears'}
                          dir={sort.key === 'serviceYears' ? sort.dir : null}
                          align="right"
                          onToggle={toggleSort}
                        >
                          Service years
                        </SortableHeader>
                        <SortableHeader
                          columnKey="vested"
                          active={sort.key === 'vested'}
                          dir={sort.key === 'vested' ? sort.dir : null}
                          align="right"
                          onToggle={toggleSort}
                        >
                          Vested
                        </SortableHeader>
                        <SortableHeader
                          columnKey="unvested"
                          active={sort.key === 'unvested'}
                          dir={sort.key === 'unvested' ? sort.dir : null}
                          align="right"
                          onToggle={toggleSort}
                        >
                          Unvested
                        </SortableHeader>
                      </>
                    )}
                    <SortableHeader
                      columnKey="total"
                      active={sort.key === 'total'}
                      dir={sort.key === 'total' ? sort.dir : null}
                      align="right"
                      onToggle={toggleSort}
                    >
                      Total
                    </SortableHeader>
                    <SortableHeader
                      columnKey="status"
                      active={sort.key === 'status'}
                      dir={sort.key === 'status' ? sort.dir : null}
                      onToggle={toggleSort}
                    >
                      Status
                    </SortableHeader>
                  </tr>
                </thead>
                <tbody>
                  {viewRows.length === 0 && isActive && (
                    <EmptyState colSpan={columnCount} onClear={clearAll} />
                  )}
                  <PaginatedRows
                    inlineRows={viewRows}
                    pagedRows={visibleEmployees}
                    showAllInline={reportMode}
                    renderRow={(emp) => {
                    const isExcludedFromMetrics = isTerminatedEmployee(emp) && !activeTerminationHandling.includeInMetrics
                    return (
                      <tr key={emp.id} data-output-employee-id={emp.id} className={isExcludedFromMetrics ? 'opacity-60' : ''}>
                        <td className="sticky-col sticky-employee-id font-mono">{emp.id}</td>
                        <td className="sticky-col sticky-employee-name">{emp.name || '-'}</td>
                        <td className="text-center">{emp.gender || '-'}</td>
                        <td className="text-center">{emp.age !== null ? emp.age : '-'}</td>
                        <td className="text-center">{emp.tenure ? emp.tenure.formatted : 'N/A'}</td>
                        <td className="whitespace-nowrap text-right font-mono">
                          {formatCurrency(emp.compensation)}
                          <ProrationBadge employee={emp} />
                        </td>
                        <td className="text-center">
                          {emp.isHCE ? (
                            <TooltipProvider>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <span className="inline-flex cursor-default items-center rounded px-1.5 py-0.5 bg-amber-50 text-amber-700 border border-amber-200">
                                    Yes{emp.hceIsEstimated && <span className="ml-0.5 text-amber-500">*</span>}
                                  </span>
                                </TooltipTrigger>
                                {emp.hceIsEstimated && (
                                  <TooltipContent className="max-w-xs">
                                    <p>Estimated – no prior-year compensation provided. Current compensation used as proxy.</p>
                                  </TooltipContent>
                                )}
                              </Tooltip>
                            </TooltipProvider>
                          ) : (
                            <span className="text-muted-foreground">
                              No{emp.hceIsEstimated && <span className="ml-0.5 text-amber-500 text-[9px]">*</span>}
                            </span>
                          )}
                        </td>
                        <td className="min-w-40 max-w-72 whitespace-normal break-words [overflow-wrap:anywhere]">{allocationGroup(emp) || '-'}</td>
                        <td className="text-right">{formatPercent(emp.totalDeferralPercent)}</td>
                        <td className="text-right font-mono">{formatCurrency(emp.actualPretax)}</td>
                        <td className="text-right font-mono">{formatCurrency(emp.actualRoth)}</td>
                        <td className="text-right font-mono">
                          {emp.catchupContribution > 0 ? (
                            formatCurrency(emp.catchupContribution)
                          ) : (
                            emp.catchupEligible ? <span className="text-muted-foreground">$0</span> : '-'
                          )}
                        </td>
                        <td className="text-right font-mono font-medium">{formatCurrency(emp.totalEmployeeContribution)}</td>
                        {matchEnabled && (
                          <td className="text-right font-mono">{formatCurrency(emp.matchAmount)}</td>
                        )}
                        {necEnabled && (
                          <td className="text-right font-mono">{formatCurrency(emp.necAmount)}</td>
                        )}
                        {qnecShown && (
                          <td className="text-right font-mono">{formatCurrency(emp.qnecAmount)}</td>
                        )}
                        {profitSharingEnabled && (
                          <td className="text-right font-mono">
                            <ProfitSharingCell employee={emp} />
                          </td>
                        )}
                        <td className="text-right font-mono">
                          {formatCurrency(emp.dcEmployerCost)}
                          {baselineById && (
                            <RowDelta value={emp.dcEmployerCost - (baselineById.get(emp.id)?.dcEmployerCost || 0)} />
                          )}
                        </td>
                        {cashBalanceEnabled && (
                          <td className="text-right font-mono text-indigo-700">
                            {emp.cashBalanceCredit > 0 ? formatCurrency(emp.cashBalanceCredit) : '-'}
                            {baselineById && (
                              <RowDelta value={(emp.cashBalanceCredit || 0) - (baselineById.get(emp.id)?.cashBalanceCredit || 0)} />
                            )}
                          </td>
                        )}
                        {afterTaxAllowed && (
                          <td className="text-right font-mono">
                            {emp.afterTaxContribution > 0 ? (
                              <span className="inline-flex items-center gap-1">
                                {formatCurrency(emp.afterTaxContribution)}
                                {emp.afterTaxLimitedBy415c && (
                                  <span className="text-amber-500" title="Reduced to stay within the 415(c) limit">*</span>
                                )}
                              </span>
                            ) : (
                              '-'
                            )}
                          </td>
                        )}
                        <td className="text-center">
                          <EligibilityBadge emp={emp} />
                        </td>
                        {activeVesting.enabled && (
                          <>
                            <td className="text-center">{emp.serviceYears ?? 'N/A'}</td>
                            <td className="whitespace-nowrap text-right font-mono text-emerald-700">
                              {emp.vestingUnresolvedAmount === emp.employerTotal && emp.employerTotal > 0
                                ? 'N/A'
                                : formatCurrency(emp.knownVestedEmployerTotal)}
                              <UnresolvedVestingBadge employee={emp} compact />
                            </td>
                            <td className="text-right font-mono text-red-700">
                              {emp.vestingUnresolvedAmount === emp.employerTotal && emp.employerTotal > 0
                                ? 'N/A'
                                : emp.knownUnvestedEmployerTotal > 0
                                  ? formatCurrency(emp.knownUnvestedEmployerTotal)
                                  : '-'}
                            </td>
                          </>
                        )}
                        <td className="text-right font-mono font-bold">
                          {formatCurrency(emp.totalContribution)}
                          {baselineById && (
                            <RowDelta value={emp.totalContribution - (baselineById.get(emp.id)?.totalContribution || 0)} />
                          )}
                        </td>
                        <td className="whitespace-nowrap text-center">
                          <div className="flex flex-nowrap items-center justify-center gap-1">
                            <span className={`inline-flex shrink-0 items-center rounded px-1.5 py-0.5 ${getEmploymentClass(emp.status)}`}>
                              {emp.status || 'Active'}
                            </span>
                            {emp.warnings?.length > 0 && (
                              <TooltipProvider>
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <span className="inline-flex shrink-0 cursor-help items-center gap-1 rounded border border-amber-200 bg-amber-50 px-1.5 py-0.5 text-[10px] font-medium text-amber-700">
                                      <AlertTriangle className="h-2.5 w-2.5" />
                                      {emp.warnings.length} warning{emp.warnings.length === 1 ? '' : 's'}
                                    </span>
                                  </TooltipTrigger>
                                  <TooltipContent className="max-w-sm">
                                    <ul className="list-disc space-y-1 pl-4">
                                      {emp.warnings.map((warning) => <li key={warning}>{warning}</li>)}
                                    </ul>
                                  </TooltipContent>
                                </Tooltip>
                              </TooltipProvider>
                            )}
                          </div>
                        </td>
                      </tr>
                    )
                    }}
                  />
                </tbody>
                <tfoot className="sticky bottom-0 z-10 font-medium">
                  <tr>
                    <td className="sticky-col sticky-employee-id">Totals:</td>
                    <td className="sticky-col sticky-employee-name"></td>
                    <td colSpan="3"></td>
                    <td className="text-right font-mono">{formatCurrency(metrics?.totalCompensation || 0)}</td>
                    <td colSpan="3"></td>
                    <td className="text-right font-mono">{formatCurrency(metrics?.totalPretaxContributions || 0)}</td>
                    <td className="text-right font-mono">{formatCurrency(metrics?.totalRothContributions || 0)}</td>
                    <td className="text-right font-mono">{formatCurrency(metrics?.totalCatchupContributions || 0)}</td>
                    <td className="text-right font-mono">{formatCurrency(metrics?.totalEmployeeContributions || 0)}</td>
                    {matchEnabled && (
                      <td className="text-right font-mono">{formatCurrency(metrics?.totalEmployerMatch || 0)}</td>
                    )}
                    {necEnabled && (
                      <td className="text-right font-mono">{formatCurrency(metrics?.totalEmployerNec || 0)}</td>
                    )}
                    {qnecShown && (
                      <td className="text-right font-mono">{formatCurrency(metrics?.totalEmployerQnec || 0)}</td>
                    )}
                    {profitSharingEnabled && (
                      <td className="text-right font-mono">{formatCurrency(metrics?.totalEmployerProfitSharing || 0)}</td>
                    )}
                    <td className="text-right font-mono">{formatCurrency(metrics?.totalEmployerContributions || 0)}</td>
                    {cashBalanceEnabled && (
                      <td className="text-right font-mono text-indigo-700">{formatCurrency(metrics?.totalCashBalanceHypotheticalCredits || 0)}</td>
                    )}
                    {afterTaxAllowed && (
                      <td className="text-right font-mono">{formatCurrency(metrics?.totalAfterTaxContributions || 0)}</td>
                    )}
                    <td></td>
                    {activeVesting.enabled && (
                      <>
                        <td></td>
                        <td className="text-right font-mono text-emerald-700">
                          {formatCurrency(metrics?.totalVestedEmployer || 0)} known
                          {(metrics?.totalVestingUnresolved || 0) > 0 && (
                            <span className="ml-1 text-[10px] text-amber-700">
                              + {formatCurrency(metrics.totalVestingUnresolved)} unresolved
                            </span>
                          )}
                        </td>
                        <td className="text-right font-mono text-red-700">{formatCurrency(metrics?.totalUnvestedEmployer || 0)} known</td>
                      </>
                    )}
                    <td className="text-right font-mono font-bold">{formatCurrency(metrics?.totalContributions || 0)}</td>
                    <td></td>
                  </tr>
                </tfoot>
              </table>
            </ExpandableScroll>
            {!reportMode && <ShowMoreBar total={viewRows.length} />}
            <div className="mt-2 flex flex-wrap items-center justify-between gap-x-4 gap-y-1 text-xs text-muted-foreground">
              <span>
                {calculatedEmployees.length} employee{calculatedEmployees.length === 1 ? '' : 's'} · Plan year {activePlan.planYear}
              </span>
              <ScrollHint when="both" />
            </div>
            </ExpandableFill>
            </Expandable>
          </CardContent>
        )}
      </Card>
    </div>
  )
}
